import { SelectField, TextField } from '@ff-it/form';
import {
  AllActivityTypesField,
  CreateScene,
  EntityAction,
  EntityFormActions,
  EntityScene,
  IndexScene,
  UpdateScene,
  useEntity,
} from 'components';
import { columns } from './columns';
import { productKindOptions } from 'options';
import { useNavigate, useRoutes } from 'react-router';
import { Form } from './Form';
import { Nav } from './Nav';
import type { EmbeddedOutletOrServiceCategory, OutletOrServiceCategory } from '../outletOrServiceCategory/types';
import { ReactElement } from 'react';
import { useSessionScope } from 'services';
import { openCatalogAction } from 'components/CatalogAction';

const initialValues = {
  // general
  kind: null,
  type: null,
  name: '',
  category: null,
  activities: [],
  // media or service
  unit: null,

  // media
  gross_price: null,

  // smart
  application: null,
  direction: null,

  // specialization
  configuration: null,
  //
  disabled: false,
};

function ProductActions(): ReactElement {
  const ctx = useEntity();
  const { slug } = useSessionScope();
  const navigate = useNavigate();
  const actions: EntityAction<any>[] = [
    {
      action: 'catalog',
      permKey: 'change',
      button: {
        icon: 'list-tree',
        children: 'Catalog',
        variant: 'secondary',
        size: 'sm',
        onClick: () =>
          openCatalogAction(ctx, (s) => {
            // dismissed
            if (!s) {
              return;
            }
            switch (s.action) {
              case 'MOVE': {
                const osc = s.target as EmbeddedOutletOrServiceCategory;
                if (osc.vendor) {
                  navigate(`/${slug}/preferences/vendors/${osc.vendor.id}/outlets/${osc.id}/products/${s.id}`);
                } else {
                  navigate(`/${slug}/preferences/service_categories/${osc.id}/products/${s.id}`);
                }
                break;
              }
              case 'CONVERT': {
                ctx.mutate();
                break;
              }
              case 'REMOVE': {
                if (!s.cascade) {
                  navigate(`../`);
                } else {
                  // We don't want to backtrack for now
                  navigate(`/${slug}/preferences`);
                }
                break;
              }
              default: {
                throw new Error(`Action not handled: ${s.action}`);
              }
            }
          }),
      },
    },
  ];

  return <EntityFormActions ml="5" actions={actions} />;
}

export function useProductRoutes(outletOrServiceCategory: OutletOrServiceCategory) {
  return useRoutes([
    {
      index: true,
      element: (
        <IndexScene
          columns={columns}
          filterFields={
            <>
              <TextField name="search" placeholder="Search.." size="sm" />
              <SelectField
                name="kind"
                placeholder="Kind"
                options={productKindOptions}
                simple
                className="ml-1"
                size="sm"
              />
              <AllActivityTypesField name="type" placeholder="Type" className="ml-1" size="sm" />
              <SelectField
                name="disabled"
                placeholder="Disabled"
                className="ml-1"
                size="sm"
                options={[
                  { label: 'Disabled', value: 'true' },
                  { label: 'Enabled', value: 'false' },
                ]}
                simple
              />
            </>
          }
        />
      ),
    },
    {
      path: 'create',
      element: (
        <CreateScene
          initialValues={{
            ...initialValues,
            type: outletOrServiceCategory.type,
          }}
        >
          <Form create kind={outletOrServiceCategory.kind} type={outletOrServiceCategory.type} />
        </CreateScene>
      ),
    },
    {
      path: ':id/',
      element: (
        <EntityScene>
          <Nav />
        </EntityScene>
      ),
      children: [
        {
          index: true,
          element: (
            <UpdateScene
              deletePath={null}
              viewDisabled
              heading={null}
              actions={<ProductActions />}
              copy={(product) => ({ ...product, id: undefined })}
            >
              <Form create={false} kind={outletOrServiceCategory.kind} type={outletOrServiceCategory.type} />
            </UpdateScene>
          ),
        },
      ],
    },
  ]);
}
